import { useState, useMemo, useEffect } from 'react';
import { 
    useSepaClientsQuery, 
    useSepaTransactionsQuery,
    useToggleInactiveMonthMutation,
    useCloseSepaMonthMutation,
    formatSepaNumber,
    getNext12Months,
    getNextFullMonth,
    calculateTotalAmount
} from '../../services/hooks/useSepaClients';
import { Client, Suscripcion } from '../../types/index';
import { useSafeMounted } from '../../services/hooks';
import { 
    Loader2, 
    ChevronUp, 
    ChevronDown, 
    Users, 
    Euro, 
    Calendar,
    Download,
    AlertTriangle,
    CheckCircle,
    Edit,
    Phone,
    Mail,
    MapPin,
    UserCheck,
    Clock
} from 'lucide-react';
import { getNextMonthAndYear } from '../../utils/utils';
import { queryClient } from '../../main';
import ConfirmationModal from '../common/ConfirmationModal';
import EditarSuscripcion from '../clientes/EditarSuscripcion';
import BeneficiarioInfo from '../clientes/BeneficiarioInfo';
import SepaFilter, { SepaFilterState } from './SepaFilter';
import DownloadExcel from './DownloadExcel';
import { toast } from 'react-toastify';
import { etiquetaSuscripcion } from '../../utils/suscripciones';

interface SepaClientsListProps {
    className?: string;
}

type ModalType = 'confirmMonth' | 'editClient' | 'editSuscripcion' | 'confirmCloseMonth' | null;

interface ModalData {
    suscripcionId?: string;
    mes?: number;
    clientId?: string;
    clientName?: string;
    suscripcion?: Suscripcion;
}

const SepaClientsList: React.FC<SepaClientsListProps> = ({ className = '' }) => {
    const isMounted = useSafeMounted();
    
    // Estados para filtros y búsqueda unificados
    const [filters, setFilters] = useState<SepaFilterState>({
        searchTerm: '',
        noBankAccount: false,
        noSubscription: false,
        inactiveNextMonth: false,
        clientsWithObservations: false,
        clientsWithReferences: false
    });
    const [sortField, setSortField] = useState<keyof Client>('nombre');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [expandedClients, setExpandedClients] = useState<string[]>([]);
    
    // Estados para modales
    const [modalType, setModalType] = useState<ModalType>(null);
    const [modalData, setModalData] = useState<ModalData>({});
    const [processingCloseMonth, setProcessingCloseMonth] = useState(false);
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);

    // Obtener datos usando React Query
    const { 
        data: clients = [], 
        isLoading: clientsLoading, 
        error: clientsError,
        isSuccess: clientsSuccess
    } = useSepaClientsQuery();
    
    const { 
        data: transactionsData, 
        isLoading: transactionsLoading, 
        error: transactionsError,
        isSuccess: transactionsSuccess
    } = useSepaTransactionsQuery();

    // Marcar cuando los datos iniciales han terminado de cargar
    useEffect(() => {
        if (clientsSuccess && transactionsSuccess && !initialDataLoaded) {
            setInitialDataLoaded(true);
        }
    }, [clientsSuccess, transactionsSuccess, initialDataLoaded]);

    // Mutations
    const toggleInactiveMonthMutation = useToggleInactiveMonthMutation();
    const closeSepaMonthMutation = useCloseSepaMonthMutation();

    // Datos derivados
    const transactions = transactionsData?.transactions || [];
    const isMonthClosed = transactionsData?.isMonthClosed || false;
    const { month: nextMonth, year: currentYear } = getNextMonthAndYear();
    const next12Months = getNext12Months();

    // Calcular el monto total actual (igual que en el Excel y backend)
    const currentTotalAmount = useMemo(() => {
        const validClients = clients.filter(client => client.suscripciones.length > 0);
        
        // Crear una copia profunda de los datos (igual que en getValidClients)
        const clientsCopy = JSON.parse(JSON.stringify(validClients));
        
        // Función auxiliar para calcular el importe con descuento
        const calcularImporteConDescuento = (suscripcion: any): number => {
            return Math.max(0, suscripcion.importeSuscripcion - (suscripcion.descuento || 0));
        };
        
        // Crear un mapa para buscar suscripciones por ID y aplicar descuentos
        const suscripcionesMap = new Map();
        clientsCopy.forEach((client: any) => {
            client.suscripciones.forEach((suscripcion: any) => {
                suscripcionesMap.set(suscripcion._id, suscripcion);
                suscripcion.importeSuscripcion = calcularImporteConDescuento(suscripcion);
            });
        });
        
        // Buscar clientes con beneficiario y sumar sus importes
        const clientsWithBeneficiario = clientsCopy.filter((client: any) =>
            client.suscripciones.some((suscripcion: any) => suscripcion.activo && suscripcion.beneficiario)
        );
        
        clientsWithBeneficiario.forEach((client: any) => {
            client.suscripciones.forEach((suscripcion: any) => {
                if (suscripcion.beneficiario) {
                    const beneficiarioSuscripcion = suscripcionesMap.get(suscripcion.beneficiario);
                    if (beneficiarioSuscripcion && !beneficiarioSuscripcion.mesesInactivos.includes(nextMonth)) {
                        // Sumar el importe del beneficiario al pagador
                        suscripcion.importeSuscripcion += beneficiarioSuscripcion.importeSuscripcion;
                    }
                }
            });
        });
        
        // Filtrar suscripciones válidas para SEPA (igual que en getValidClients)
        const sepaClientsData = clientsCopy.flatMap((client: any) => 
            client.suscripciones
                .filter((suscripcion: any) => {
                    const isInactive = suscripcion.mesesInactivos?.includes(nextMonth) || false;
                    const hasIban = suscripcion.iban || (suscripcion.pagador && suscripcion.pagador.IBAN);
                    const isNotBeneficiario = !suscripcion.pagador_id; // Excluir beneficiarios
                    return suscripcion.activo && !isInactive && hasIban && isNotBeneficiario;
                })
                .map((suscripcion: any) => ({
                    cliente_id: client._id,
                    suscripcion_id: suscripcion._id,
                    iban: (suscripcion.pagador?.IBAN) || suscripcion.iban || '',
                    importe: suscripcion.importeSuscripcion, // Ya incluye descuentos y beneficiarios
                    observaciones: suscripcion.observaciones || ''
                }))
        );
        
        return sepaClientsData.reduce((total: number, client: any) => total + client.importe, 0);
    }, [clients, nextMonth]);

    // Obtener número de clientes que se procesarán
    const clientsToProcessCount = useMemo(() => {
        return clients.filter(client => 
            client.suscripciones.some(suscripcion => {
                const isInactive = suscripcion.mesesInactivos?.includes(nextMonth) || false;
                const hasIban = suscripcion.iban || (suscripcion.pagador && suscripcion.pagador.IBAN);
                return !isInactive && hasIban;
            })
        ).length;
    }, [clients, nextMonth]);

    // Estadísticas
    const stats = useMemo(() => {
        const totalClients = clients.length;
        const totalAmount = calculateTotalAmount(clients);
        
        // Lógica correcta: Un cliente tiene IBAN si tiene al menos una suscripción con:
        // - suscripcion.iban (Domiciliación) O
        // - suscripcion.pagador_id (Pagador - Cliente) O  
        // - suscripcion.pagador && suscripcion.pagador.IBAN (Pagador - No Cliente)
        const clientsWithIban = clients.filter(client => 
            client.suscripciones.some(sub => 
                sub.iban || 
                sub.pagador_id || 
                (sub.pagador && sub.pagador.IBAN)
            )
        ).length;
        
        return {
            totalClients,
            totalAmount,
            clientsWithIban,
            clientsWithoutIban: totalClients - clientsWithIban
        };
    }, [clients]);

    // Filtrar y ordenar clientes
    const filteredClients = useMemo(() => {
        if (!clients) return [];
        
        return clients
            .filter((client: Client) => {
                // Filtro por término de búsqueda
                const searchMatch = 
                    client.nombre.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
                    client.dni.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
                    (client.email && client.email.toLowerCase().includes(filters.searchTerm.toLowerCase())) ||
                    client.telefono.includes(filters.searchTerm) ||
                    client.suscripciones.some(sub => 
                        (sub.iban && sub.iban.toLowerCase().includes(filters.searchTerm.toLowerCase())) ||
                        (sub.observaciones && sub.observaciones.toLowerCase().includes(filters.searchTerm.toLowerCase()))
                    );
                
                // Filtros avanzados
                const matchesBankAccount = filters.noBankAccount ? (
                    // Un cliente "sin IBAN" es aquel que NO tiene ninguna suscripción con IBAN válido
                    // Debe coincidir exactamente con la lógica del componente original
                    client.suscripciones.length > 0 && 
                    !client.suscripciones.some(suscripcion => 
                        suscripcion.iban || 
                        suscripcion.pagador_id || 
                        (suscripcion.pagador && suscripcion.pagador.IBAN)
                    )
                ) : true;
                
                const matchesNoSubscription = filters.noSubscription ? client.suscripciones.length === 0 : true;
                
                const matchesInactiveNextMonth = filters.inactiveNextMonth ? (
                    client.suscripciones.length > 0 &&
                    client.suscripciones.some(suscripcion => suscripcion.mesesInactivos?.includes(nextMonth) || false)
                ) : true;

                const matchesWithObservations = filters.clientsWithObservations ? (
                    client.suscripciones.length > 0 && 
                    client.suscripciones.some(suscripcion => suscripcion.observaciones && suscripcion.observaciones !== "")
                ) : true;

                const matchesWithReferences = filters.clientsWithReferences ? (!!client.observaciones) : true;
                
                return searchMatch && matchesBankAccount && matchesNoSubscription && 
                       matchesInactiveNextMonth && matchesWithObservations && matchesWithReferences;
            })
            .sort((a: Client, b: Client) => {
                if (sortField === 'nombre') {
                    return sortDirection === 'asc' 
                        ? a.nombre.localeCompare(b.nombre)
                        : b.nombre.localeCompare(a.nombre);
                }
                return 0;
            });
    }, [clients, filters, sortField, sortDirection, nextMonth]);

    // Función para manejar cambios de filtros desde SepaFilter
    const handleFiltersChange = (newFilters: SepaFilterState) => {
        setFilters(newFilters);
    };

    // Handlers
    const handleSort = (field: keyof Client) => {
        if (field === sortField) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const toggleClientExpansion = (clientId: string) => {
        setExpandedClients(prev => 
            prev.includes(clientId) 
                ? prev.filter(id => id !== clientId)
                : [...prev, clientId]
        );
    };

    const handleToggleInactiveMonth = (suscripcionId: string, mes: number) => {
        setModalData({ suscripcionId, mes });
        setModalType('confirmMonth');
    };

    const confirmToggleMonth = async () => {
        if (!modalData.suscripcionId || !modalData.mes) return;
        
        await toggleInactiveMonthMutation.mutateAsync({
            suscripcionId: modalData.suscripcionId,
            mes: modalData.mes
        });
        
        setModalType(null);
        setModalData({});
    };

    const handleEditClient = (clientId: string, clientName: string) => {
        setModalData({ clientId, clientName });
        setModalType('editClient');
        // TODO: Abrir modal de edición o navegar a página de edición
        toast.info(`Función de editar cliente ${clientName} - Por implementar`);
        setModalType(null);
    };

    const handleEditSuscripcion = (clientId: string, suscripcion: Suscripcion) => {
        setModalData({ clientId, suscripcion });
        setModalType('editSuscripcion');
    };

    // Callback para invalidar queries SEPA cuando se edita una suscripción desde este contexto
    // Esto es necesario porque EditarSuscripcion por defecto solo invalida queries de cliente ['client']
    // pero SepaClientsList usa queries específicas ['sepa-clients'] que también necesitan actualizarse
    const handleSuscripcionUpdated = () => {
        queryClient.invalidateQueries({ queryKey: ['sepa-clients'] });
    };

    const handleCloseMonth = () => {
        if (isMonthClosed) {
            toast.info("El mes ya está cerrado.");
            return;
        }
        
        // DEBUGGING: Calcular suma total de TODOS los importes (menos descuentos) sin filtros
        const validClients = clients.filter(client => client.suscripciones.length > 0);
        
        const allImportes = validClients.flatMap(client => 
            client.suscripciones.map(suscripcion => 
                suscripcion.importeSuscripcion - (suscripcion.descuento || 0)
            )
        );
        
        const totalAllImportes = allImportes.reduce((sum, importe) => sum + importe, 0);
        // console.log('🔍 DEBUG - TOTAL DE TODOS LOS IMPORTES (sin filtros):', totalAllImportes.toFixed(2), '€');
        // console.log('🔍 DEBUG - Número total de suscripciones:', allImportes.length);
        
        // Abrir modal de confirmación
        setModalType('confirmCloseMonth');
    };

    const confirmCloseMonth = async () => {
        setProcessingCloseMonth(true);
        setModalType(null); // Cerrar modal

        try {
            // Usar la misma lógica que en getValidClients (Excel)
            const validClients = clients.filter(client => client.suscripciones.length > 0);
            
            // Crear una copia profunda y aplicar lógica de beneficiarios
            const clientsCopy = JSON.parse(JSON.stringify(validClients));
            
            // Función auxiliar para calcular el importe con descuento
            const calcularImporteConDescuento = (suscripcion: any): number => {
                return Math.max(0, suscripcion.importeSuscripcion - (suscripcion.descuento || 0));
            };
            
            // Crear un mapa para buscar suscripciones por ID y aplicar descuentos
            const suscripcionesMap = new Map();
            clientsCopy.forEach((client: any) => {
                client.suscripciones.forEach((suscripcion: any) => {
                    suscripcionesMap.set(suscripcion._id, suscripcion);
                    suscripcion.importeSuscripcion = calcularImporteConDescuento(suscripcion);
                });
            });
            
            // Buscar clientes con beneficiario y sumar sus importes
            const clientsWithBeneficiario = clientsCopy.filter((client: any) =>
                client.suscripciones.some((suscripcion: any) => suscripcion.activo && suscripcion.beneficiario)
            );
            
            clientsWithBeneficiario.forEach((client: any) => {
                client.suscripciones.forEach((suscripcion: any) => {
                    if (suscripcion.beneficiario) {
                        const beneficiarioSuscripcion = suscripcionesMap.get(suscripcion.beneficiario);
                        if (beneficiarioSuscripcion && !beneficiarioSuscripcion.mesesInactivos.includes(nextMonth)) {
                            // Sumar el importe del beneficiario al pagador
                            suscripcion.importeSuscripcion += beneficiarioSuscripcion.importeSuscripcion;
                        }
                    }
                });
            });
            
            // Filtrar suscripciones válidas para SEPA (igual que en getValidClients)
            const sepaClientsData = clientsCopy.flatMap((client: any) => 
                client.suscripciones
                    .filter((suscripcion: any) => {
                        const isInactive = suscripcion.mesesInactivos?.includes(nextMonth) || false;
                        const hasIban = suscripcion.iban || (suscripcion.pagador && suscripcion.pagador.IBAN);
                        const isNotBeneficiario = !suscripcion.pagador_id; // Excluir beneficiarios
                        return suscripcion.activo && !isInactive && hasIban && isNotBeneficiario;
                    })
                    .map((suscripcion: any) => ({
                        cliente_id: client._id,
                        suscripcion_id: suscripcion._id,
                        iban: (suscripcion.pagador?.IBAN) || suscripcion.iban || '',
                        importe: suscripcion.importeSuscripcion, // Ya incluye descuentos y beneficiarios
                        observaciones: suscripcion.observaciones || ''
                    }))
            );

            await closeSepaMonthMutation.mutateAsync({
                mes: nextMonth,
                anio: currentYear,
                sepaClients: sepaClientsData
            });

        } catch (error) {
            console.error('Error in confirmCloseMonth:', error);
        } finally {
            setProcessingCloseMonth(false);
        }
    };

    // Loading state
    if (clientsLoading || transactionsLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                <span className="ml-2 text-gray-600">Cargando datos SEPA...</span>
            </div>
        );
    }

    // Error state
    if (clientsError || transactionsError) {
        return (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                <AlertTriangle className="w-5 h-5 inline mr-2" />
                Error al cargar los datos SEPA
            </div>
        );
    }

    return (
        <div className={`bg-white rounded-lg shadow ${className}`}>
            {/* Panel de estadísticas */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4 border-b bg-blue-50">
                <div className="flex items-center justify-center md:justify-start p-3 rounded-lg bg-white shadow-sm">
                    <Users className="h-8 w-8 text-blue-500 mr-3" />
                    <div>
                        <div className="text-xs text-gray-500">Total Clientes</div>
                        <div className="text-xl font-bold">{stats.totalClients}</div>
                    </div>
                </div>
                
                <div className="flex items-center justify-center md:justify-start p-3 rounded-lg bg-white shadow-sm">
                    <Euro className="h-8 w-8 text-green-500 mr-3" />
                    <div>
                        <div className="text-xs text-gray-500">Importe Total</div>
                        <div className="text-xl font-bold">€{stats.totalAmount}</div>
                    </div>
                </div>
                
                <div className="flex items-center justify-center md:justify-start p-3 rounded-lg bg-white shadow-sm">
                    <CheckCircle className="h-8 w-8 text-green-500 mr-3" />
                    <div>
                        <div className="text-xs text-gray-500">Con IBAN</div>
                        <div className="text-xl font-bold">{stats.clientsWithIban}</div>
                    </div>
                </div>
                
                <div className="flex items-center justify-center md:justify-start p-3 rounded-lg bg-white shadow-sm">
                    <AlertTriangle className="h-8 w-8 text-red-500 mr-3" />
                    <div>
                        <div className="text-xs text-gray-500">Sin IBAN</div>
                        <div className="text-xl font-bold">{stats.clientsWithoutIban}</div>
                    </div>
                </div>
            </div>

            {/* Estado del mes */}
            <div className="p-4 border-b">
                <div className="flex items-center justify-between">
                    <div className="flex items-center">
                        <Calendar className="h-5 w-5 text-blue-500 mr-2" />
                        <span className="font-medium">
                            Próximo mes a cobrar: {getNextFullMonth()} {currentYear}
                        </span>
                        {isMonthClosed && (
                            <span className="ml-3 px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm">
                                Mes Cerrado
                            </span>
                        )}
                    </div>
                    
                    <div className="flex items-center space-x-2">
                        <DownloadExcel clients={clients} transactionStatus={isMonthClosed} />
                        
                        <button
                            onClick={handleCloseMonth}
                            disabled={isMonthClosed || processingCloseMonth || clientsLoading || transactionsLoading || !initialDataLoaded || clients.length === 0}
                            className={`flex items-center px-4 py-2 text-sm rounded-lg transition-colors ${
                                isMonthClosed || processingCloseMonth || clientsLoading || transactionsLoading || !initialDataLoaded || clients.length === 0
                                    ? 'bg-gray-500 text-white cursor-not-allowed'
                                    : 'bg-red-500 text-white hover:bg-red-600'
                            }`}
                            title={
                                isMonthClosed ? 'El mes ya está cerrado' :
                                processingCloseMonth ? 'Procesando cierre de mes...' :
                                clientsLoading || transactionsLoading ? 'Cargando datos...' :
                                !initialDataLoaded ? 'Esperando carga inicial de datos...' :
                                clients.length === 0 ? 'No hay clientes para procesar' :
                                'Cerrar mes - Esta acción no se puede deshacer'
                            }
                        >
                            {processingCloseMonth ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                                <Calendar className="h-4 w-4 mr-2" />
                            )}
                            Cerrar Mes
                        </button>
                    </div>
                </div>
            </div>

            {/* Filtros y búsqueda */}
            <div className="p-4 border-b">
                <SepaFilter onFilterChange={handleFiltersChange} />
            </div>

            {/* Lista de clientes */}
            <div className="overflow-x-auto">
                {filteredClients.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        No se encontraron clientes SEPA
                    </div>
                ) : (
                    <div className="min-w-full">
                        {/* Header - Solo en desktop */}
                        <div className="hidden lg:grid lg:grid-cols-12 gap-4 p-4 bg-gray-50 border-b font-medium text-sm text-gray-700">
                            <div className="col-span-2">
                                <button
                                    onClick={() => handleSort('nombre')}
                                    className="flex items-center hover:text-blue-600"
                                >
                                    Cliente
                                    {sortField === 'nombre' && (
                                        sortDirection === 'asc' ? 
                                        <ChevronUp className="h-4 w-4 ml-1" /> : 
                                        <ChevronDown className="h-4 w-4 ml-1" />
                                    )}
                                </button>
                            </div>
                            <div className="col-span-2">IBAN</div>
                            <div className="col-span-1">Importe</div>
                            <div className="col-span-2">Observaciones</div>
                            <div className="col-span-4">Próximos 12 meses</div>
                            <div className="col-span-1">Editar</div>
                        </div>

                        {/* Clientes */}
                        {filteredClients.map((client) => (
                            <div key={client._id} className="border-b">
                                {client.suscripciones.map((suscripcion, index) => (
                                    <div key={`${client._id}-${index}`}>
                                        {/* Desktop layout */}
                                        <div className="hidden lg:block">
                                            <div className="grid grid-cols-12 gap-4 p-4 hover:bg-gray-50">
                                                {/* min-w-0 en los dos niveles: sin el, un hijo de flex
                                                    no baja de su ancho intrinseco y el correo largo se
                                                    salia por encima de la columna del IBAN. */}
                                                <div className="col-span-2 min-w-0">
                                                    <div className="flex items-center min-w-0">
                                                        <button
                                                            onClick={() => toggleClientExpansion(`${client._id}-${index}`)}
                                                            className="text-blue-500 p-1 mr-2 shrink-0"
                                                        >
                                                            {expandedClients.includes(`${client._id}-${index}`) ?
                                                                <ChevronUp className="h-4 w-4" /> :
                                                                <ChevronDown className="h-4 w-4" />
                                                            }
                                                        </button>
                                                        <div className="min-w-0">
                                                            <div className="font-medium truncate" title={client.nombre}>{client.nombre}</div>
                                                            <div className="text-sm text-gray-500 truncate">{client.dni}</div>
                                                            {/* select-all: al truncar se pierde poder arrastrar
                                                                para seleccionar, asi que un solo clic selecciona
                                                                el valor entero y se puede copiar. */}
                                                            {client.email && (
                                                                <div className="text-sm text-gray-500 truncate select-all" title={client.email}>{client.email}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <div className="col-span-2 min-w-0">
                                                    {(() => {
                                                        // Lógica para mostrar el IBAN correcto según el tipo de pago
                                                        let ibanToShow = '';
                                                        let ibanSource = '';
                                                        
                                                        if (suscripcion.iban) {
                                                            // Domiciliación - IBAN del cliente
                                                            ibanToShow = suscripcion.iban;
                                                            ibanSource = 'Cliente';
                                                        } else if (suscripcion.pagador && suscripcion.pagador.IBAN) {
                                                            // Pagador - No Cliente - IBAN del pagador
                                                            ibanToShow = suscripcion.pagador.IBAN;
                                                            ibanSource = 'Pagador';
                                                        } else if (suscripcion.pagador_id) {
                                                            // Pagador - Cliente - buscar el IBAN del otro cliente
                                                            const clientePagador = clients.find(c => 
                                                                c.suscripciones.some(s => s._id === suscripcion.pagador_id)
                                                            );
                                                            if (clientePagador) {
                                                                const suscripcionPagador = clientePagador.suscripciones.find(s => s._id === suscripcion.pagador_id);
                                                                if (suscripcionPagador?.iban) {
                                                                    ibanToShow = suscripcionPagador.iban;
                                                                    ibanSource = `${clientePagador.nombre}`;
                                                                } else {
                                                                    ibanToShow = 'IBAN no disponible';
                                                                    ibanSource = 'Cliente Pagador';
                                                                }
                                                            } else {
                                                                ibanToShow = 'Cliente no encontrado';
                                                                ibanSource = 'Error';
                                                            }
                                                        }
                                                        
                                                        return ibanToShow ? (
                                                            <div className="min-w-0">
                                                                {/* El IBAN completo, en el title: 24 caracteres no
                                                                    caben en esta columna a menos de ~1.400px. */}
                                                                <div
                                                                    className="font-mono text-sm bg-gray-100 p-2 rounded truncate select-all"
                                                                    title={ibanToShow}
                                                                >
                                                                    {ibanToShow}
                                                                </div>
                                                                <div className="text-xs text-gray-500 mt-1 truncate">
                                                                    ({ibanSource})
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <span className="text-red-500 text-sm">Sin IBAN</span>
                                                        );
                                                    })()}
                                                </div>
                                                
                                                <div className="col-span-1">
                                                    <div className="font-medium">
                                                        €{formatSepaNumber(suscripcion.importeSuscripcion - (suscripcion.descuento || 0))}
                                                    </div>
                                                    {(suscripcion.descuento ?? 0) > 0 && (
                                                        <div className="text-sm text-green-600">
                                                            Desc: €{formatSepaNumber(suscripcion.descuento)}
                                                        </div>
                                                    )}
                                                </div>
                                                
                                                <div className="col-span-2">
                                                    {suscripcion.observaciones && (
                                                        <div className="text-sm bg-yellow-50 p-2 rounded border-l-4 border-yellow-400">
                                                            {suscripcion.observaciones}
                                                        </div>
                                                    )}
                                                </div>
                                                
                                                <div className="col-span-4">
                                                    <div className="grid grid-cols-12 gap-1">
                                                        {next12Months.map((month) => {
                                                            const isInactive = suscripcion.mesesInactivos?.includes(month.index) || false;
                                                            return (
                                                                <button
                                                                    key={month.index}
                                                                    onClick={() => handleToggleInactiveMonth(suscripcion._id, month.index)}
                                                                    className={`text-xs p-1 rounded transition-colors ${
                                                                        isInactive 
                                                                            ? 'bg-red-500 text-white hover:bg-red-600' 
                                                                            : 'bg-green-500 text-white hover:bg-green-600'
                                                                    }`}
                                                                    title={`${month.name} - ${isInactive ? 'Inactivo' : 'Activo'}`}
                                                                >
                                                                    {month.name}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                                
                                                <div className="col-span-1">
                                                    <button
                                                        onClick={() => handleEditSuscripcion(client._id, suscripcion)}
                                                        className="text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-50"
                                                        title="Editar suscripción"
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </div>
                                            
                                            {/* Detalles expandibles para desktop */}
                                            {expandedClients.includes(`${client._id}-${index}`) && (
                                                <div className="bg-gray-50 p-4 border-t">
                                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                        {/* Información de contacto */}
                                                        <div className="bg-white p-4 rounded shadow-sm">
                                                            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                                                                <Phone className="h-4 w-4 mr-2 text-blue-500" />
                                                                Información de Contacto
                                                            </h4>
                                                            <div className="space-y-2 text-sm">
                                                                <div>
                                                                    <span className="text-gray-500">Teléfono:</span>
                                                                    <span className="ml-2 font-medium">{client.telefono}</span>
                                                                </div>
                                                                {client.email && (
                                                                    <div>
                                                                        <span className="text-gray-500">Email:</span>
                                                                        <span className="ml-2">{client.email}</span>
                                                                    </div>
                                                                )}
                                                                <div>
                                                                    <span className="text-gray-500">DNI:</span>
                                                                    <span className="ml-2 font-mono">{client.dni}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-gray-500">Fecha de alta:</span>
                                                                    <span className="ml-2">{new Date(client.fechaAlta).toLocaleDateString()}</span>
                                                                </div>
                                                                {client.createdBy && (
                                                                    <div>
                                                                        <span className="text-gray-500">Registrado por:</span>
                                                                        <span className="ml-2">{client.createdBy}</span>
                                                                    </div>
                                                                )}
                                                                {client.observaciones && (
                                                                    <div>
                                                                        <span className="text-gray-500">Referencias:</span>
                                                                        <span className="ml-2">{client.observaciones}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Detalles de suscripción */}
                                                        <div className="bg-white p-4 rounded shadow-sm">
                                                            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                                                                <Euro className="h-4 w-4 mr-2 text-green-500" />
                                                                Detalles de Suscripción
                                                            </h4>
                                                            <div className="space-y-2 text-sm">
                                                                <div>
                                                                    <span className="text-gray-500">Suscripción:</span>
                                                                    <span className="ml-2 font-medium">{etiquetaSuscripcion(suscripcion.nombreSuscripcion)}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-gray-500">Tipo de Pago:</span>
                                                                    <span className="ml-2">{suscripcion.tipoPago}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-gray-500">Frecuencia:</span>
                                                                    <span className="ml-2">{suscripcion.frecuencia}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-gray-500">Importe:</span>
                                                                    <span className="ml-2 font-medium">€{formatSepaNumber(suscripcion.importeSuscripcion)}</span>
                                                                </div>
                                                                {(suscripcion.descuento ?? 0) > 0 && (
                                                                    <div>
                                                                        <span className="text-gray-500">Descuento:</span>
                                                                        <span className="ml-2 text-green-600 font-medium">€{formatSepaNumber(suscripcion.descuento)}</span>
                                                                    </div>
                                                                )}
                                                                <div>
                                                                    <span className="text-gray-500">Estado:</span>
                                                                    <span className={`ml-2 px-2 py-1 rounded-full text-xs ${suscripcion.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                                        {suscripcion.activo ? 'Activa' : 'Inactiva'}
                                                                    </span>
                                                                </div>
                                                                <div className="pt-1 border-t">
                                                                    <span className="text-gray-500">Total:</span>
                                                                    <span className="ml-2 font-bold text-lg">€{formatSepaNumber(suscripcion.importeSuscripcion - (suscripcion.descuento || 0))}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Información del Beneficiario */}
                                                        {isMounted.current && suscripcion.beneficiario && (
                                                            <div className="bg-white p-4 rounded shadow-sm">
                                                                <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                                                                    <UserCheck className="h-4 w-4 mr-2 text-green-500" />
                                                                    Información del Beneficiario
                                                                </h4>
                                                                <BeneficiarioInfo beneficiarioId={suscripcion.beneficiario} />
                                                            </div>
                                                        )}
                                                        
                                                        {/* Plazas Reservadas */}
                                                        {client.plazas && client.plazas.length > 0 && (
                                                            <div className="bg-white p-4 rounded shadow-sm">
                                                                <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                                                                    <Clock className="h-4 w-4 mr-2 text-indigo-500" />
                                                                    Plazas Reservadas ({client.plazas.length})
                                                                </h4>
                                                                <div className="space-y-2 text-sm">
                                                                    {client.plazas.map((plaza, plazaIndex) => (
                                                                        <div key={plaza._id || plazaIndex} className="bg-indigo-50 p-2 rounded">
                                                                            <div className="flex justify-between items-start">
                                                                                <div className="flex-1">
                                                                                    <div className="font-medium text-indigo-800">
                                                                                        {plaza.dia} - {plaza.horaInicio}
                                                                                    </div>
                                                                                    <div className="text-indigo-600 text-xs">
                                                                                        Nivel: {typeof plaza.nivel === 'object' ? plaza.nivel.nombre : plaza.nivel || 'No especificado'}
                                                                                    </div>
                                                                                </div>
                                                                                {plaza.enEspera && (
                                                                                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                                                                                        En espera
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Información del pagador */}
                                                        {suscripcion.pagador && (
                                                            <div className="bg-white p-4 rounded shadow-sm">
                                                                <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                                                                    <Users className="h-4 w-4 mr-2 text-purple-500" />
                                                                    Información del Pagador
                                                                </h4>
                                                                <div className="space-y-2 text-sm">
                                                                    <div>
                                                                        <span className="text-gray-500">Nombre:</span>
                                                                        <span className="ml-2 font-medium">{suscripcion.pagador.nombre}</span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-gray-500">DNI:</span>
                                                                        <span className="ml-2 font-mono">{suscripcion.pagador.DNI}</span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-gray-500">IBAN:</span>
                                                                        <span className="ml-2 font-mono bg-green-50 px-2 py-1 rounded">
                                                                            {suscripcion.pagador.IBAN ? suscripcion.pagador.IBAN : 'No especificado'}
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-gray-500">Teléfono:</span>
                                                                        <span className="ml-2">{suscripcion.pagador.telefono}</span>
                                                                    </div>
                                                                    {suscripcion.pagador.email && (
                                                                        <div>
                                                                            <span className="text-gray-500">Email:</span>
                                                                            <span className="ml-2">{suscripcion.pagador.email}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Información del pagador - Cliente */}
                                                        {suscripcion.pagador_id && (
                                                            <div className="bg-white p-4 rounded shadow-sm">
                                                                <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                                                                    <Users className="h-4 w-4 mr-2 text-orange-500" />
                                                                    Cliente Pagador
                                                                </h4>
                                                                <div className="space-y-2 text-sm">
                                                                    {(() => {
                                                                        // Buscar el cliente pagador en la lista de clientes
                                                                        const clientePagador = clients.find(c => 
                                                                            c.suscripciones.some(s => s._id === suscripcion.pagador_id)
                                                                        );
                                                                        
                                                                        if (clientePagador) {
                                                                            const suscripcionPagador = clientePagador.suscripciones.find(s => s._id === suscripcion.pagador_id);
                                                                            return (
                                                                                <>
                                                                                    <div>
                                                                                        <span className="text-gray-500">Nombre:</span>
                                                                                        <span className="ml-2 font-medium">{clientePagador.nombre}</span>
                                                                                    </div>
                                                                                    <div>
                                                                                        <span className="text-gray-500">DNI:</span>
                                                                                        <span className="ml-2 font-mono">{clientePagador.dni}</span>
                                                                                    </div>
                                                                                    <div>
                                                                                        <span className="text-gray-500">Email:</span>
                                                                                        <span className="ml-2">{clientePagador.email || 'No especificado'}</span>
                                                                                    </div>
                                                                                    <div>
                                                                                        <span className="text-gray-500">Teléfono:</span>
                                                                                        <span className="ml-2">{clientePagador.telefono}</span>
                                                                                    </div>
                                                                                    <div>
                                                                                        <span className="text-gray-500">IBAN:</span>
                                                                                        <span className="ml-2 font-mono bg-orange-50 px-2 py-1 rounded">
                                                                                            {suscripcionPagador?.iban || 'No especificado'}
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className="pt-2 border-t text-xs text-gray-600">
                                                                                        Este cliente paga la suscripción de {client.nombre}
                                                                                    </div>
                                                                                </>
                                                                            );
                                                                        } else {
                                                                            return (
                                                                                <div className="text-gray-500 italic">
                                                                                    Cliente pagador no encontrado (ID: {suscripcion.pagador_id})
                                                                                </div>
                                                                            );
                                                                        }
                                                                    })()}
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        {/* TODO: Información de plazas */}
                                                        {/* Aquí se podrían agregar las plazas cuando esté disponible el componente */}
                                                        
                                                        {/* Observaciones de Suscripción */}
                                                        {suscripcion.observaciones && (
                                                            <div className="bg-white p-4 rounded shadow-sm">
                                                                <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                                                                    <AlertTriangle className="h-4 w-4 mr-2 text-yellow-500" />
                                                                    Observaciones
                                                                </h4>
                                                                <div className="space-y-3 text-sm">
                                                                    <div className="bg-yellow-50 p-2 rounded">
                                                                        <div className="font-medium text-yellow-700">Suscripción:</div>
                                                                        <div>{suscripcion.observaciones}</div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Mobile layout */}
                                        <div className="lg:hidden p-4">
                                            <div className="flex justify-between items-start mb-3">
                                                <div>
                                                    <div className="font-medium">{client.nombre}</div>
                                                    <div className="text-sm text-gray-500">{client.dni}</div>
                                                </div>
                                                <button
                                                    onClick={() => toggleClientExpansion(`${client._id}-${index}`)}
                                                    className="text-blue-500 p-1"
                                                >
                                                    {expandedClients.includes(`${client._id}-${index}`) ? 
                                                        <ChevronUp className="h-4 w-4" /> : 
                                                        <ChevronDown className="h-4 w-4" />
                                                    }
                                                </button>
                                            </div>
                                            
                                            <div className="space-y-2">
                                                <div className="flex justify-between">
                                                    <span className="text-sm text-gray-500">Importe:</span>
                                                    <span className="font-medium">
                                                        €{formatSepaNumber(suscripcion.importeSuscripcion - (suscripcion.descuento || 0))}
                                                    </span>
                                                </div>
                                                
                                                {(() => {
                                                    // Lógica para mostrar el IBAN correcto según el tipo de pago
                                                    let ibanToShow = '';
                                                    let ibanSource = '';
                                                    
                                                    if (suscripcion.iban) {
                                                        ibanToShow = suscripcion.iban;
                                                        ibanSource = 'Cliente';
                                                    } else if (suscripcion.pagador && suscripcion.pagador.IBAN) {
                                                        ibanToShow = suscripcion.pagador.IBAN;
                                                        ibanSource = 'Pagador';
                                                    } else if (suscripcion.pagador_id) {
                                                        // Pagador - Cliente - buscar el IBAN del otro cliente
                                                        const clientePagador = clients.find(c => 
                                                            c.suscripciones.some(s => s._id === suscripcion.pagador_id)
                                                        );
                                                        if (clientePagador) {
                                                            const suscripcionPagador = clientePagador.suscripciones.find(s => s._id === suscripcion.pagador_id);
                                                            if (suscripcionPagador?.iban) {
                                                                ibanToShow = suscripcionPagador.iban;
                                                                ibanSource = clientePagador.nombre;
                                                            } else {
                                                                ibanToShow = 'IBAN no disponible';
                                                                ibanSource = 'Cliente Pagador';
                                                            }
                                                        }
                                                    }
                                                    
                                                    return ibanToShow && (
                                                        <div className="flex justify-between">
                                                            <span className="text-sm text-gray-500">IBAN ({ibanSource}):</span>
                                                            <span className="font-mono text-sm">{ibanToShow}</span>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                            
                                            {expandedClients.includes(`${client._id}-${index}`) && (
                                                <div className="mt-4 pt-4 border-t space-y-4">
                                                    {/* Información de contacto del cliente */}
                                                    <div className="bg-blue-50 p-3 rounded">
                                                        <div className="text-sm font-medium text-blue-800 mb-2">Información del Cliente:</div>
                                                        
                                                        {client.email && (
                                                            <div className="flex items-center text-sm text-gray-600 mb-1">
                                                                <Mail className="h-4 w-4 mr-2 text-blue-500" />
                                                                {client.email}
                                                            </div>
                                                        )}
                                                        
                                                        <div className="flex items-center text-sm text-gray-600 mb-1">
                                                            <Phone className="h-4 w-4 mr-2 text-blue-500" />
                                                            {client.telefono}
                                                        </div>
                                                        
                                                        {/* Dirección si existe en el tipo Client */}
                                                        {(client as any).direccion && (
                                                            <div className="flex items-center text-sm text-gray-600 mb-1">
                                                                <MapPin className="h-4 w-4 mr-2 text-blue-500" />
                                                                {(client as any).direccion}
                                                            </div>
                                                        )}
                                                        
                                                        {/* Fecha de alta */}
                                                        <div className="text-sm text-gray-600 mb-1">
                                                            <span className="font-medium">Fecha de alta:</span> {new Date(client.fechaAlta).toLocaleDateString()}
                                                        </div>
                                                        
                                                        {/* Creado por */}
                                                        {client.createdBy && (
                                                            <div className="text-sm text-gray-600 mb-1">
                                                                <span className="font-medium">Registrado por:</span> {client.createdBy}
                                                            </div>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Observaciones de la suscripción */}
                                                    {suscripcion.observaciones && (
                                                        <div className="bg-yellow-50 p-3 rounded border-l-4 border-yellow-400">
                                                            <div className="text-sm font-medium text-yellow-800">Observaciones de Suscripción:</div>
                                                            <div className="text-sm text-yellow-600 mt-1">{suscripcion.observaciones}</div>
                                                        </div>
                                                    )}
                                                    
                                                    {/* Detalles de la suscripción */}
                                                    <div className="bg-green-50 p-3 rounded">
                                                        <div className="text-sm font-medium text-green-800 mb-2">Detalles de Suscripción:</div>
                                                        <div className="grid grid-cols-2 gap-2 text-sm">
                                                            <div>
                                                                <span className="text-gray-500">Importe:</span>
                                                                <span className="font-medium ml-2">€{formatSepaNumber(suscripcion.importeSuscripcion)}</span>
                                                            </div>
                                                            {(suscripcion.descuento ?? 0) > 0 && (
                                                                <div>
                                                                    <span className="text-gray-500">Descuento:</span>
                                                                    <span className="font-medium ml-2 text-green-600">€{formatSepaNumber(suscripcion.descuento)}</span>
                                                                </div>
                                                            )}
                                                            <div>
                                                                <span className="text-gray-500">Total:</span>
                                                                <span className="font-bold ml-2">€{formatSepaNumber(suscripcion.importeSuscripcion - (suscripcion.descuento || 0))}</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-gray-500">Tipo:</span>
                                                                <span className="ml-2">{suscripcion.tipoPago}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Información del pagador en móvil */}
                                                    {suscripcion.pagador && (
                                                        <div className="bg-purple-50 p-3 rounded">
                                                            <div className="text-sm font-medium text-purple-800 mb-2">Información del Pagador:</div>
                                                            <div className="space-y-2 text-sm">
                                                                <div>
                                                                    <span className="text-gray-500">Nombre:</span>
                                                                    <span className="font-medium ml-2">{suscripcion.pagador.nombre}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-gray-500">DNI:</span>
                                                                    <span className="font-mono ml-2">{suscripcion.pagador.DNI}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-gray-500">IBAN:</span>
                                                                    <span className="font-mono ml-2 bg-white px-2 py-1 rounded text-xs">
                                                                        {suscripcion.pagador.IBAN ? suscripcion.pagador.IBAN : 'No especificado'}
                                                                    </span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-gray-500">Teléfono:</span>
                                                                    <span className="ml-2">{suscripcion.pagador.telefono}</span>
                                                                </div>
                                                                {suscripcion.pagador.email && (
                                                                    <div>
                                                                        <span className="text-gray-500">Email:</span>
                                                                        <span className="ml-2">{suscripcion.pagador.email}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                    
                                                    {/* Información del pagador - Cliente en móvil */}
                                                    {suscripcion.pagador_id && (
                                                        <div className="bg-orange-50 p-3 rounded">
                                                            <div className="text-sm font-medium text-orange-800 mb-2">Cliente Pagador:</div>
                                                            <div className="space-y-2 text-sm">
                                                                {(() => {
                                                                    // Buscar el cliente pagador en la lista de clientes
                                                                    const clientePagador = clients.find(c => 
                                                                        c.suscripciones.some(s => s._id === suscripcion.pagador_id)
                                                                    );
                                                                    
                                                                    if (clientePagador) {
                                                                        const suscripcionPagador = clientePagador.suscripciones.find(s => s._id === suscripcion.pagador_id);
                                                                        return (
                                                                            <>
                                                                                <div>
                                                                                    <span className="text-gray-500">Nombre:</span>
                                                                                    <span className="font-medium ml-2">{clientePagador.nombre}</span>
                                                                                </div>
                                                                                <div>
                                                                                    <span className="text-gray-500">DNI:</span>
                                                                                    <span className="font-mono ml-2">{clientePagador.dni}</span>
                                                                                </div>
                                                                                <div>
                                                                                    <span className="text-gray-500">Email:</span>
                                                                                    <span className="ml-2">{clientePagador.email || 'No especificado'}</span>
                                                                                </div>
                                                                                <div>
                                                                                    <span className="text-gray-500">Teléfono:</span>
                                                                                    <span className="ml-2">{clientePagador.telefono}</span>
                                                                                </div>
                                                                                <div>
                                                                                    <span className="text-gray-500">IBAN:</span>
                                                                                    <span className="font-mono ml-2 bg-white px-2 py-1 rounded text-xs">
                                                                                        {suscripcionPagador?.iban || 'No especificado'}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="pt-2 border-t text-xs text-gray-600">
                                                                                    Este cliente paga por {client.nombre}
                                                                                </div>
                                                                            </>
                                                                        );
                                                                    } else {
                                                                        return (
                                                                            <div className="text-gray-500 italic text-xs">
                                                                                Cliente pagador no encontrado
                                                                            </div>
                                                                        );
                                                                    }
                                                                })()}
                                                            </div>
                                                        </div>
                                                    )}
                                                    
                                                    <div>
                                                        <div className="text-sm font-medium mb-2">Próximos 12 meses:</div>
                                                        <div className="grid grid-cols-6 gap-2">
                                                            {next12Months.map((month) => {
                                                                const isInactive = suscripcion.mesesInactivos?.includes(month.index) || false;
                                                                return (
                                                                    <button
                                                                        key={month.index}
                                                                        onClick={() => handleToggleInactiveMonth(suscripcion._id, month.index)}
                                                                        className={`text-xs p-2 rounded transition-colors ${
                                                                            isInactive 
                                                                                ? 'bg-red-500 text-white' 
                                                                                : 'bg-green-500 text-white'
                                                                        }`}
                                                                    >
                                                                        {month.name}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="flex justify-end">
                                                        <button
                                                            onClick={() => handleEditSuscripcion(client._id, suscripcion)}
                                                            className="flex items-center text-blue-500 hover:text-blue-700 px-3 py-2 rounded hover:bg-blue-50"
                                                        >
                                                            <Edit className="h-4 w-4 mr-2" />
                                                            Editar Suscripción
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modales */}
            {modalType === 'confirmMonth' && (
                <ConfirmationModal
                    isOpen={true}
                    onClose={() => setModalType(null)}
                    onConfirm={confirmToggleMonth}
                    confirmMessage="¿Estás seguro de que quieres cambiar el estado de este mes?"
                />
            )}

            {modalType === 'confirmCloseMonth' && (
                <ConfirmationModal
                    isOpen={true}
                    onClose={() => setModalType(null)}
                    onConfirm={confirmCloseMonth}
                    confirmMessage={`⚠️ CERRAR MES SEPA - ${getNextFullMonth()} ${currentYear}

🔒 Esta acción NO SE PUEDE DESHACER y procesará todos los cobros SEPA del mes.

📊 Se procesarán ${clientsToProcessCount} clientes con IBAN válido.

💰 Importe total: €${formatSepaNumber(currentTotalAmount)}

¿Estás seguro de que quieres proceder?`}
                />
            )}

            {modalType === 'editSuscripcion' && modalData.suscripcion && modalData.clientId && (
                <EditarSuscripcion 
                    suscripcion={modalData.suscripcion} 
                    onClose={() => setModalType(null)} 
                    clientId={modalData.clientId}
                    // Callback específico para invalidar queries SEPA desde este contexto
                    // Necesario porque este componente usa ['sepa-clients'] en lugar de ['client']
                    onSuscripcionUpdated={handleSuscripcionUpdated}
                />
            )}
        </div>
    );
};

export default SepaClientsList;